// Fatia S1: primeiro acesso (negócio → objetivo → orçamento → conexões → diagnóstico).
// Passo de conexões usa o componente da S2 e o diagnóstico o da S3b.
import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight, Check, Sparkles, TrendingUp } from 'lucide-react'
import { brl, type CompanyDto, type Goal } from '@impulso/shared'
import type { UpdateCompanyRequest } from '@impulso/shared/api/tenancy'
import { api, errorMessage } from '../../lib/api'
import { useCan, useCompany } from '../../lib/company'
import { useSession } from '../../lib/session'
import ConnectAccountsPanel from '../../features/connections/ConnectAccountsPanel'
import TopSuggestions from '../../features/recommendations/TopSuggestions'
import { FormError } from '../../features/auth/AuthShell'
import { estimate, GOAL_OPTIONS, SEGMENTS, suggestedGoal } from '../../features/onboarding/constants'
import '../../features/onboarding/onboarding.css'

const STEPS = ['Seu negócio', 'Seu objetivo', 'Seu orçamento', 'Suas contas', 'Diagnóstico']

export default function Onboarding() {
  const company = useCompany()
  const canEdit = useCan('company.edit_settings')
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { refresh } = useSession()

  const [params] = useSearchParams()
  // Volta do OAuth (?selecionar / ?conectado / ?erro): retoma no passo de conexão, onde a escolha da conta aparece.
  const returningFromOAuth = ['selecionar', 'conectado', 'erro'].some((k) => params.has(k))
  const [step, setStep] = useState(returningFromOAuth ? 3 : 0)
  const [name, setName] = useState(company.name)
  const knownSegment = company.segment && SEGMENTS.includes(company.segment)
  const [segment, setSegment] = useState<string>(company.segment ? (knownSegment ? company.segment : 'Outro') : '')
  const [otherSegment, setOtherSegment] = useState(company.segment && !knownSegment ? company.segment : '')
  const [city, setCity] = useState(company.city ?? '')
  const [goal, setGoal] = useState<Goal>(company.goal ?? 'whatsapp')
  const [budget, setBudget] = useState(company.monthlyBudget ?? 1500)
  const [ticket, setTicket] = useState<number>(company.avgTicket ?? 80)
  const [closePer10, setClosePer10] = useState(company.conversionRate != null ? Math.round(company.conversionRate * 10) : 4)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { window.scrollTo?.(0, 0) }, [step])

  if (!canEdit) return <Navigate to={`/app/${company.id}/inicio`} replace />

  const segmentValue = segment === 'Outro' ? otherSegment.trim() || 'Outro' : segment
  const est = estimate(budget, ticket || 0, closePer10)
  const canNext = step !== 0 || (name.trim().length >= 2 && !!segment)
  const goalOpt = GOAL_OPTIONS.find((g) => g.id === goal) ?? GOAL_OPTIONS[0]!

  async function save(patch: UpdateCompanyRequest) {
    const updated = await api.patch<CompanyDto>(`/companies/${company.id}`, patch)
    qc.setQueryData(['company', company.id], updated)
  }

  async function next() {
    setError(null)
    setSaving(true)
    try {
      if (step === 0) {
        await save({ name: name.trim(), segment: segmentValue, city: city.trim() || null })
        // Primeira vez: já deixa marcado o objetivo que combina com o ramo (SaaS → cadastros, loja virtual → vendas no site).
        if (!company.goal) setGoal(suggestedGoal(segment))
      }
      if (step === 1) await save({ goal })
      if (step === 2) await save({ monthlyBudget: budget, avgTicket: ticket || null, conversionRate: closePer10 / 10 })
      setStep(step + 1)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  async function finish() {
    setError(null)
    setSaving(true)
    try {
      const updated = await api.post<CompanyDto>(`/companies/${company.id}/onboarding/complete`)
      qc.setQueryData(['company', company.id], updated)
      await refresh()
      navigate(`/app/${company.id}/inicio`, { replace: true })
    } catch (err) {
      setError(errorMessage(err))
      setSaving(false)
    }
  }

  return (
    <div className="ob-embed">
      <aside className="ob-side">
        <div className="logo" style={{ padding: 0, color: 'white' }}>
          <span className="logo-mark" style={{ background: 'white', color: 'var(--primary)' }}><img src="/logo.png" alt="" style={{ width: 22, height: 22 }} /></span>
          Seu Impulso
        </div>
        <div>
          <h2 style={{ fontSize: 22, lineHeight: 1.3 }}>Anúncios no Google, no Instagram e no TikTok, sem precisar virar especialista.</h2>
          <p className="muted mt-12">Em poucos minutos a gente entende seu negócio, conecta suas contas e mostra onde dá para melhorar.</p>
        </div>
        <div className="ob-steps">
          {STEPS.map((s, i) => (
            <div key={s} className={`ob-step ${i === step ? 'on' : ''} ${i < step ? 'done' : ''}`}>
              <span className="n">{i < step ? <Check size={14} /> : i + 1}</span>{s}
            </div>
          ))}
        </div>
        <p className="small muted" style={{ marginTop: 'auto' }}>Sem fidelidade. Você paga os anúncios direto ao Google, à Meta e ao TikTok.</p>
      </aside>

      <section className="ob-main" aria-live="polite">
        <div className="small muted">Passo {step + 1} de {STEPS.length} · {STEPS[step]}</div>
        <div className="progress mt-8 ob-mobile-steps" aria-hidden><span style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} /></div>
        <div className="mt-12"><FormError error={error} /></div>

        {step === 0 && (
          <div className="stack mt-12" style={{ gap: 20 }}>
            <h1>Conta pra gente sobre o seu negócio</h1>
            <div className="field"><label htmlFor="ob-name">Nome do negócio</label><input id="ob-name" className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="field">
              <label>O que você faz?</label>
              <div className="row wrap" style={{ gap: 8 }} role="radiogroup" aria-label="Ramo do negócio">
                {SEGMENTS.map((s) => <button type="button" role="radio" aria-checked={segment === s} key={s} className={`chip ${segment === s ? 'on' : ''}`} onClick={() => setSegment(s)}>{s}</button>)}
              </div>
              {segment === 'Outro' && <input className="input mt-8" placeholder="Qual é o seu ramo?" aria-label="Qual é o seu ramo" value={otherSegment} onChange={(e) => setOtherSegment(e.target.value)} />}
            </div>
            <div className="field">
              <label htmlFor="ob-city">Cidade</label>
              <input id="ob-city" className="input" placeholder="Ex.: Belo Horizonte, MG" aria-describedby="ob-city-hint" value={city} onChange={(e) => setCity(e.target.value)} />
              <span id="ob-city-hint" className="small muted">Vende pela internet para o Brasil todo? Pode deixar em branco.</span>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="stack mt-12" style={{ gap: 20 }}>
            <h1>O que você mais quer agora?</h1>
            <p className="muted">Dá para mudar depois. Isso nos ajuda a montar as primeiras campanhas e sugestões.</p>
            <div className="grid g-2" role="radiogroup" aria-label="Objetivo principal">
              {GOAL_OPTIONS.map((g) => (
                <button type="button" role="radio" aria-checked={goal === g.id} key={g.id} className={`option-card ${goal === g.id ? 'on' : ''}`} onClick={() => setGoal(g.id)}>
                  <span className="ic purple"><g.icon size={18} /></span>
                  <span><span className="strong">{g.title}</span><br /><span className="small muted">{g.hint}</span></span>
                  <span className="radio" />
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="stack mt-12" style={{ gap: 20 }}>
            <h1>Quanto dá para investir por mês?</h1>
            <p className="muted">Pode começar pequeno. Esse é o teto: o Seu Impulso nunca passa desse valor.</p>
            <div className="card">
              <label htmlFor="ob-budget" className="small strong">Investimento mensal em anúncios</label>
              <div className="row wrap" style={{ gap: 16 }}>
                <span style={{ fontSize: 28, fontWeight: 700, minWidth: 130 }}>{brl(budget)}</span>
                <input id="ob-budget" type="range" className="range" min={300} max={20000} step={100} value={budget} onChange={(e) => setBudget(+e.target.value)} style={{ flex: 1, minWidth: 160 }} />
              </div>
              <div className="grid g-2 mt-16" style={{ gap: 16 }}>
                <div className="field">
                  <label htmlFor="ob-ticket">{goal === 'leads' ? 'Quanto vale um cliente novo? (R$, ex.: um ano de assinatura)' : 'Quanto um cliente costuma gastar com você? (R$)'}</label>
                  <input id="ob-ticket" className="input" type="number" min={0} step={1} inputMode="decimal" value={ticket} onChange={(e) => setTicket(+e.target.value)} />
                </div>
                <div className="field">
                  <label>{goalOpt.close}</label>
                  <div className="ob-close-row" role="radiogroup" aria-label={goalOpt.close}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => <button type="button" role="radio" aria-checked={closePer10 === n} key={n} className={`chip ${closePer10 === n ? 'on' : ''}`} onClick={() => setClosePer10(n)}>{n}</button>)}
                  </div>
                </div>
              </div>
            </div>
            <div className="callout green">
              <TrendingUp size={18} color="var(--green)" style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                Estimativa simples: {brl(budget)} por mês costuma trazer <b>entre {est.low} e {est.high} {goalOpt.noun}</b>. Se {closePer10} em cada 10 {goal === 'leads' ? 'virarem clientes' : 'comprarem'},
                são cerca de <b>{brl(est.salesLow)} a {brl(est.salesHigh)}</b> em vendas. Depois de conectar suas contas, a conta passa a usar os seus números reais.
              </span>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="stack mt-12" style={{ gap: 16 }}>
            <h1>Conecte suas contas</h1>
            <p className="muted">A conexão é feita pelas páginas oficiais do Google, da Meta e do TikTok. Você escolhe o que liberar e pode desconectar quando quiser.</p>
            <ConnectAccountsPanel companyId={company.id} />
            <p className="small muted">Ainda não tem conta de anúncios? <Link className="strong" style={{ color: 'var(--primary)' }} to={`/app/${company.id}/contas/pagamento`}>Veja como criar e pagar</Link>. Pode pular este passo: dá para conectar depois em “Contas conectadas”.</p>
          </div>
        )}

        {step === 4 && (
          <div className="stack mt-12" style={{ gap: 16 }}>
            <h1>Pronto, {name.trim() || company.name}!</h1>
            <div className="callout">
              <Sparkles size={18} color="var(--primary)" style={{ flexShrink: 0, marginTop: 2 }} />
              <span>Estamos analisando suas contas; as sugestões aparecem aqui e no painel. Isso pode levar alguns minutos depois da conexão.</span>
            </div>
            <TopSuggestions companyId={company.id} limit={4} />
          </div>
        )}

        <div className="row between ob-actions" style={{ marginTop: 32, gap: 10, flexWrap: 'wrap' }}>
          {step > 0 ? <button type="button" className="btn" disabled={saving} onClick={() => setStep(step - 1)}><ArrowLeft size={16} /> Voltar</button> : <Link className="btn ghost" to={`/app/${company.id}/inicio`}>Fazer depois</Link>}
          {step === 3 && <button type="button" className="btn ghost" onClick={() => setStep(4)}>Pular por agora</button>}
          {step < 4 ? (
            <button type="button" className="btn primary lg" disabled={!canNext || saving} onClick={next}>{saving ? 'Salvando…' : 'Continuar'} <ArrowRight size={16} /></button>
          ) : (
            <button type="button" className="btn primary lg" disabled={saving} onClick={finish}>{saving ? 'Abrindo…' : 'Ir para o painel'} <ArrowRight size={16} /></button>
          )}
        </div>
      </section>
    </div>
  )
}
